import { Component, Inject, Input, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth.service';
import { SharedService } from '../../../services/shared.service';
import { MenuItem, MessageService } from 'primeng/api';
import { LocalStorageKeys } from '../../models/user';
import { Subscription } from 'rxjs';
import { AppConfig } from '../../../utils/app-config';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  @Input() title = '';
  @Input() tagline = '';
  linkedInDropItems: MenuItem[] = [];
  instaDropItems: MenuItem[] = [];
  twitterDropItems: MenuItem[] = [];
  facebookItems: MenuItem[] = [];
  userData: any;
  isCompanyAdmin = false;
  isEmployee = false;
  sidebarCollapsed = false;
  linkedInButton: any;
  facebookButton: any;
  facebookBusinessButton: any;
  instaButton: any;
  twitterButton: any;

  private readonly CLIENT_ID = AppConfig.getConfig().CLIENT_ID;
  private readonly REDIRECT_URI = AppConfig.getConfig().redirect_url;
  private readonly FB_CONSUMER_ID = AppConfig.getConfig().FB_CONSUMER_ID;
  private readonly FB_BUSINESS_ID = AppConfig.getConfig().FB_BUSINESS_ID;
  private readonly fb_redirect_url = AppConfig.getConfig().fb_redirect_url;
  private readonly INSTA_CONSUMER_ID = AppConfig.getConfig().INSTA_CONSUMER_ID;
  private readonly insta_redirect_url = AppConfig.getConfig().insta_redirect_url;
  private readonly TWITTER_CONSUMER_ID = AppConfig.getConfig().TWITTER_CONSUMER_ID;
  private readonly twitter_redirect_url = AppConfig.getConfig().twitter_redirect_url;

  readonly auth_url = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${this.CLIENT_ID}&redirect_uri=${this.REDIRECT_URI}&scope=w_member_social%20r_basicprofile`;
  readonly fb_consumer_url = `https://www.facebook.com/v22.0/dialog/oauth?redirect_uri=${this.fb_redirect_url}&response_type=code&scope=public_profile,email,user_posts&client_id=${this.FB_CONSUMER_ID}`;
  readonly fb_business_url = `https://www.facebook.com/v22.0/dialog/oauth?redirect_uri=${this.fb_redirect_url}&response_type=code&scope=public_profile,email,pages_show_list,pages_read_engagement&client_id=${this.FB_BUSINESS_ID}`;
  readonly instaAuth_url = `https://www.instagram.com/oauth/authorize?client_id=${this.INSTA_CONSUMER_ID}&redirect_uri=${this.insta_redirect_url}&response_type=code&scope=instagram_business_basic`;
  readonly twitterAuth_url = `http://twitter.com/i/oauth2/authorize?response_type=code&client_id=${this.TWITTER_CONSUMER_ID}&redirect_uri=${this.twitter_redirect_url}&scope=tweet.read%20users.read%20offline.access&state=state&code_challenge=dYVda9VOVEbMifkANCjiG9qn9yqRbdXujZJYPzS1pos&code_challenge_method=S256`;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private sharedService: SharedService,
    private renderer: Renderer2,
    private messageService: MessageService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.initializeUserRoles();
    this.initializeLinkedInMenuItems();
    this.initializeFacebookMenuItems();
    this.initializeInstaMenuItems();
    this.subscribeToSharedService();
    this.initializeTwitterMenuItems();
  } 

  private initializeUserRoles(): void {
    this.isCompanyAdmin = this.authService.isCompanyAdmin();
    this.isEmployee = this.authService.isEmployee();
  }

  private initializeLinkedInMenuItems(): void {
    if(this.linkedInButton !== 'true'){
      this.linkedInDropItems = [
        {
          label: 'Connect LinkedIn',
          command: () => this.connect()
        }
      ];
    }
    else{
      this.linkedInDropItems = [
        {
          label: 'Disconnect LinkedIn',
          command: () => this.disconnectLinkedIn()
        }
      ];
    }
  }

  private initializeFacebookMenuItems(): void {
    this.facebookItems = [];
    
    // Add consumer option
    if(this.facebookButton !== 'true'){
      this.facebookItems.push({
        label: 'Connect as consumer',
        command: () => this.connectFacebook(1)
      });
    } else {
      this.facebookItems.push({
        label: 'Disconnect Facebook (Consumer)',
        command: () => this.disconnectFacebook(1)
      });
    }
    
    // Add business option
    if(this.facebookBusinessButton !== 'true'){
      this.facebookItems.push({
        label: 'Connect as business',
        command: () => this.connectFacebook(2)
      });
    } else {
      this.facebookItems.push({
        label: 'Disconnect Facebook (Business)',
        command: () => this.disconnectFacebook(2)
      });
    }
  }

  private initializeInstaMenuItems():void{
    if(this.instaButton !== 'true'){
      this.instaDropItems = [
        {
          label: 'Connect Instagram',
          command: () => this.connectInsta()
        }
      ];
    }
    else{
      this.instaDropItems = [
        {
          label: 'Disconnect Instagram',
          command: () => this.disconnectInsta()
        }
      ];
    }
  }

  private initializeTwitterMenuItems():void{
    if(this.twitterButton !== 'true'){
      this.twitterDropItems = [
        {
          label: 'Connect Twitter',
          command: () => this.connectTwitter()
        }
      ];
    }
    else{
      this.twitterDropItems = [
        {
          label: 'Disconnect Twitter',
          command: () => this.disconnectTwitter()
        }
      ];
    }
  }

  private subscribeToSharedService(): void {
    const isConnectedSub = this.sharedService.isConnected$.subscribe(isConnected => {
      this.linkedInButton = isConnected;
      this.initializeLinkedInMenuItems();
    });

    const isFbConnectedSub = this.sharedService.isFbConnected$.subscribe(isFbConnected => {
      this.facebookButton = isFbConnected;
      this.initializeFacebookMenuItems();
    });

    const isFbBusinessConnectedSub = this.sharedService.isFbBusinessConnected$.subscribe(isFbBusinessConnected => {
      this.facebookBusinessButton = isFbBusinessConnected;
      this.initializeFacebookMenuItems();
    });

    const isInstaConnectedSub = this.sharedService.isInstaConnected$.subscribe(isInstaConnected => {
      this.instaButton = isInstaConnected;
      this.initializeInstaMenuItems();
    });

    const isTwitterConnectedSub = this.sharedService.isTwitterConnected$.subscribe(isTwitterConnected => {
      this.twitterButton = isTwitterConnected;
      this.initializeTwitterMenuItems();
    });

    this.subscriptions.add(isConnectedSub);
    this.subscriptions.add(isFbConnectedSub);
    this.subscriptions.add(isFbBusinessConnectedSub);
    this.subscriptions.add(isInstaConnectedSub);
    this.subscriptions.add(isTwitterConnectedSub);
  }

  toggleSideBar(): void {
    const bodyClassList = this.document.body.classList;
    this.sidebarCollapsed = bodyClassList.contains('sidebarClose');
    this.renderer[this.sidebarCollapsed ? 'removeClass' : 'addClass'](this.document.body, 'sidebarClose');
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  connect(): void {
    this.openUrl(this.auth_url);
  }

  disconnectFacebook(accountType: number){
    let queryToBeSend = {
      "code": '',
      "account_type": accountType
    }
    this.authService.disconnectFacebook(queryToBeSend).subscribe({
      next: ( data ) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `${data.detail}` });
        if(accountType === 1) {
          this.sharedService.updateFbConnected('false');
        } else {
          this.sharedService.updateFbBusinessConnected('false');
        }
      },
      error: ({error}: any) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `${error.detail}` });
      }
    })
  }

  disconnectLinkedIn(){
    let queryToBeSend = {
      "code": '',
      "account_type": 1
    }
    this.authService.disconnectLinkedIn(queryToBeSend).subscribe({
      next: ( data ) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `${data.detail}` });
        this.sharedService.updateLnkdConnected('false'); 
      },
      error: ({error}: any) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `${error.detail}` });
      }
    })
  }

  connectInsta(): void {
    this.openUrl(this.instaAuth_url);
  }

  disconnectInsta(){
    let queryToBeSend = {
      "code": ''
    }
    this.authService.disconnectInsta(queryToBeSend).subscribe({
      next: ( data ) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `${data.detail}` });
        this.sharedService.updateInstaConnected('false'); 
      },
      error: ({error}: any) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `${error.detail}` });
      }
    })
  }

  disconnectTwitter(){
    let queryToBeSend = {
      "code": ''
    }
    this.authService.disconnectTwitter(queryToBeSend).subscribe({
      next: ( data ) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `${data.detail}` });
        this.sharedService.updateTwitterConnected('false'); 
      },
      error: ({error}: any) =>{
        this.sharedService.setLoader(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `${error.detail}` });
      }
    })
  }

  connectTwitter(): void {
    this.openUrl(this.twitterAuth_url);
  }

  private connectFacebook(accountType: number): void {
    const url = accountType === 1 ? this.fb_consumer_url : this.fb_business_url;
    window.localStorage.setItem(LocalStorageKeys.FbAccType, accountType.toString());
    this.openUrl(url);
  }

  private openUrl(url: string): void {
    window.open(url, '_blank');
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
